import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/auth';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';

export function SubconsciousChatPage() {
  const { sessionId } = useParams({ from: '/subconscious/$sessionId' });
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['subconscious', sessionId],
    queryFn: () => api.subconscious.getSession(sessionId),
    refetchInterval: 5000, // Poll for new messages
    enabled: !!user,
  });

  const sendMessage = useMutation({
    mutationFn: (content: string) => api.subconscious.sendMessage(sessionId, content),
    onSuccess: () => {
      setMessage('');
      queryClient.invalidateQueries({ queryKey: ['subconscious', sessionId] });
    },
  });

  const pauseSession = useMutation({
    mutationFn: () => api.subconscious.pause(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subconscious', sessionId] });
    },
  });

  const resumeSession = useMutation({
    mutationFn: () => api.subconscious.resume(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subconscious', sessionId] });
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !sendMessage.isPending) {
      sendMessage.mutate(message.trim());
    }
  };

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-white mb-4">Sign in to access the subconscious</h1>
        <Link to="/login" className="btn-primary">Login</Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="card animate-pulse">
          <div className="h-8 bg-dream-700 rounded w-1/3 mb-4" />
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-dream-700 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-white mb-4">Session not found</h1>
        <Link to="/subconscious" className="btn-primary">Back to Sessions</Link>
      </div>
    );
  }

  const { session, messages } = data;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <Link to="/subconscious" className="text-dream-400 hover:text-white text-sm mb-1 block">
            &larr; Back to sessions
          </Link>
          <h1 className="text-xl font-display font-bold text-white">
            {session.dreamScenario || 'Subconscious Session'}
          </h1>
          <div className="flex items-center gap-4 mt-1">
            <span
              className={clsx(
                'text-xs px-2 py-0.5 rounded',
                session.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
              )}
            >
              {session.status}
            </span>
            {session.meditationUnlocked && (
              <span className="text-xs text-purple-400">Meditation Access Unlocked</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {session.status === 'active' ? (
            <button onClick={() => pauseSession.mutate()} className="btn-secondary text-sm">
              Pause
            </button>
          ) : (
            <button onClick={() => resumeSession.mutate()} className="btn-primary text-sm">
              Resume
            </button>
          )}
        </div>
      </div>

      {/* Characters */}
      {session.dreamCharacters && session.dreamCharacters.length > 0 && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-dream-900/50 rounded-lg border border-dream-800">
          <span className="text-dream-400 text-sm">Participants:</span>
          <div className="flex gap-2">
            {session.dreamCharacters.map((char) => (
              <span
                key={char.id}
                className="text-sm px-2 py-1 bg-purple-900/30 text-purple-300 rounded"
                title={char.personality}
              >
                {char.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4">
        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {session.status === 'active' && (
        <form onSubmit={handleSubmit} className="flex gap-3">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Speak to your subconscious..."
            className="input flex-1"
            disabled={sendMessage.isPending}
          />
          <button
            type="submit"
            disabled={!message.trim() || sendMessage.isPending}
            className="btn-primary px-6"
          >
            {sendMessage.isPending ? 'Sending...' : 'Send'}
          </button>
        </form>
      )}
    </div>
  );
}

function ChatMessage({
  message,
}: {
  message: {
    id: string;
    createdAt: string;
    senderType: string;
    senderName?: string;
    content: string;
  };
}) {
  const messageClasses = {
    user: 'chat-message-user',
    user_twin: 'chat-message-twin',
    dream_character: 'chat-message-character',
    narrator: 'chat-message-narrator',
    system: 'chat-message-narrator text-xs',
  };

  return (
    <div className={messageClasses[message.senderType as keyof typeof messageClasses] || 'chat-message-twin'}>
      {message.senderName && message.senderType !== 'user' && message.senderType !== 'narrator' && (
        <div
          className={clsx(
            'text-xs mb-1',
            message.senderType === 'dream_character' ? 'text-purple-400' : 'text-dream-400'
          )}
        >
          {message.senderName}
        </div>
      )}
      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
      <div className="text-xs text-dream-500 mt-1">
        {formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}
      </div>
    </div>
  );
}
